import styled from '@emotion/styled';
import Alert from 'storybookConfig/stories/cells/Alert';
import Modal from 'storybookConfig/stories/cells/Modal';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Spinner from 'storybookConfig/stories/molecules/Spinner';
import renderNoticeBody from 'storybookConfig/stories/species/PolicyNotice/renderNoticeBody';
import type { PolicyNotice } from 'types/models/policy';

const ErrorWrapper = styled.div`
  margin-top: 16px;
`;

// Counsel-approved copy must render verbatim, so undo the design-system Button's capitalize.
const AcknowledgeButton = styled(PrimaryButton)`
  text-transform: none;
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export interface PolicyNoticeViewProps {
  /**
   * The notice to show; the modal is hidden when there is none
   */
  notice?: PolicyNotice;
  /**
   * Called when the acknowledge button is clicked
   */
  onAcknowledge: () => void;
  /**
   * Disables the button and shows a spinner while the acknowledgement is saving
   */
  isPosting: boolean;
  /**
   * Shows the inline error asking the user to try again
   */
  hasError: boolean;
  /**
   * Id of the title element, which labels the dialog
   */
  titleId: string;
}

/**
 * The policy notice dialog. Presentational only: `PolicyNoticeModal` supplies the notice and
 * records events.
 */
const PolicyNoticeView = ({
  notice,
  onAcknowledge,
  isPosting,
  hasError,
  titleId,
}: PolicyNoticeViewProps) => (
  <Modal.Root
    show={!!notice}
    centered
    closeOnBackdropClick={false}
    trapFocus
    role="dialog"
    aria-modal
    aria-labelledby={titleId}
  >
    <Modal.Header>
      <Modal.Title>
        {/* Modal.Title takes no id, so an inline element inside it labels the dialog. */}
        <span id={titleId}>{notice?.title}</span>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {notice && renderNoticeBody(notice.body)}
      {hasError && (
        <ErrorWrapper role="alert">
          <Alert type="error">Something went wrong, please try again.</Alert>
        </ErrorWrapper>
      )}
    </Modal.Body>
    <Modal.Footer>
      <AcknowledgeButton onClick={onAcknowledge} disabled={isPosting}>
        <ButtonContent>
          {isPosting && <Spinner size="16px" color="white" />}
          {notice?.buttonLabel}
        </ButtonContent>
      </AcknowledgeButton>
    </Modal.Footer>
  </Modal.Root>
);

export default PolicyNoticeView;
